import { useGridApiRef } from '@mui/x-data-grid-premium';
import { formDataToObject, ICommonDataGrid, IIdName, ISharedConfig, setSnackbar } from '@shared/library';
import axios, { AxiosError } from 'axios';
import React from "react";
import config from '../../../../../config';
import "./InterventionExport.scss";
//#region ICONS
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PreviewRoundedIcon from '@mui/icons-material/PreviewRounded';
//#endregion
//#region COMPONENTS
import GestDataGridPro from "@shared/library/dist/components/gest/GestDataGrid/GestDataGridPro";
import GestToggleButtonGroup from '@shared/library/dist/components/gest/GestInputs/button/group/GestLabelButtonGroup';
import GestDatePicker from '@shared/library/dist/components/gest/GestInputs/pickers/date/GestDatePicker';
import GestSelect from "@shared/library/dist/components/gest/GestInputs/select/GestSelect";
import GestTextArea from "@shared/library/dist/components/gest/GestInputs/text area/GestTextArea";
import { CustomDetails } from "../../../../_gest";
//#endregion
//MODELS
import { IGestSelectOption } from '@shared/library/dist/components/gest/GestInputs/select/GestSelectProps';

interface IExportQuery extends IIdName {
    parameters: string[];
}

const InterventionExport = (props: { lang: any; config: ISharedConfig; }) => {
    const gridApiRef = useGridApiRef();

    //#region STATES
    //FORM
    const [exports, setExports] = React.useState<IGestSelectOption<string[]>[]>([]);
    const [selectedExport, setSelectedExport] = React.useState<IGestSelectOption<string[]>>();
    const form = React.useRef<HTMLFormElement>(null);

    //PREVIEW
    const [exportPreview, setExportPreview] = React.useState<ICommonDataGrid<any>>();

    //FLAGS
    const [exportLoading, setExportLoading] = React.useState(false);
    //#endregion

    //#region METHODS

    //#region API CALLS
    const getExports = () => {
        setExportLoading(true);
        axios.get<IExportQuery[]>(`${config.api.internal}/WFM/Interventions/Exports`, { withCredentials: true })
            .then(res => {
                const options = res.data.map<IGestSelectOption<string[]>>(v => ({
                    value: v.id,
                    label: v.name,
                    data: v.parameters
                }));
                setExports(options);
            })
            .catch((err: AxiosError) => console.error(err))
            .finally(() => setExportLoading(false));
    };

    const getExportPreview = () => {
        if (selectedExport?.value) {
            const params = form.current ? formDataToObject<any>(form.current) : undefined;
            setExportLoading(true);
            return axios.get<ICommonDataGrid<any>>(`${config.api.internal}/WFM/Interventions/Export/${selectedExport.value}/Preview`, { withCredentials: true, params })
                .then(res => {
                    setExportPreview(res.data);
                    return res.data;
                })
                .catch((err: AxiosError) => {
                    console.error(err);
                    return undefined;
                })
                .finally(() => setExportLoading(false));
        }
    };
    //#endregion

    const exportFile = (format: 'csv' | 'excel', data?: ICommonDataGrid<any>) => {
        if (data?.InitRows?.length) {
            //attende il render della griglia con le nuove righe
            setTimeout(() => {
                const fileName = String(selectedExport?.label ?? "export");
                format === 'excel'
                    ? gridApiRef.current?.exportDataAsExcel({ fileName })
                    : gridApiRef.current?.exportDataAsCsv({ fileName, delimiter: ";" });
            });
        }
        else {
            setSnackbar(props.config, !data?.InitRows ? { message: "Errore durante richiesta risultati esportazione", severity: 'error' } : { message: "Esportazione vuota. Riprova variando i parametri", severity: 'warning' });
        }
    };
    //#endregion

    //STATES CHANGE
    React.useEffect(getExports, []);

    return <div id="interventionExport" className='flex-column'>
        <GestSelect
            required
            label={props.lang.select_exportation}
            value={selectedExport?.value}
            options={exports}
            onchange={selected => {
                setSelectedExport(selected[0]);
                setExportPreview(undefined);
            }}
            disabled={exportLoading}
            w={250}
        />

        {selectedExport?.data?.length ?
            <CustomDetails
                variant="add-on"
                isOpen={Boolean(selectedExport?.data?.length)}
                title={props.lang.parameters}
            >
                <form ref={form} onSubmit={getExportPreview} className='flex-row'>
                    {selectedExport.data.map(p => {
                        const normalized = p.replaceAll('@', '');
                        const paramName = props.lang[normalized] ?? normalized;

                        return p.toLowerCase().includes('_date')
                            ? <GestDatePicker label={paramName} name={normalized} w={130} noFuture />
                            : <GestTextArea label={paramName} name={normalized} />;
                    })}
                </form>
            </CustomDetails>
            : <></>}

        <GestDataGridPro
            apiRef={gridApiRef}
            lang={props.lang}
            columns={exportPreview?.Columns ?? []}
            rows={exportPreview?.InitRows}
            loading={exportLoading}
            w={"75vw"}
        />

        <GestToggleButtonGroup
            btnw={200}
            btnh={40}
            alignment='right'
            buttons={[
                {
                    tooltip: !selectedExport ? props.lang.select_exportation : undefined,
                    label: props.lang.preview,
                    icon: <PreviewRoundedIcon />,
                    onclick: getExportPreview,
                    loading: exportLoading,
                    disabled: !selectedExport,
                },
                {
                    tooltip: !selectedExport ? props.lang.select_exportation : !exportPreview?.InitRows?.length ? "Nessun risultato da esportare" : undefined,
                    label: "Download CSV",
                    icon: <DownloadRoundedIcon />,
                    onclick: () => exportPreview ? exportFile('csv', exportPreview) : getExportPreview()?.then(d => exportFile('csv', d)),
                    disabled: !selectedExport,
                },
                {
                    tooltip: !selectedExport ? props.lang.select_exportation : !exportPreview?.InitRows?.length ? "Nessun risultato da esportare" : undefined,
                    label: "Download Excel",
                    icon: <DownloadRoundedIcon />,
                    onclick: () => exportPreview ? exportFile('excel', exportPreview) : getExportPreview()?.then(d => exportFile('excel', d)),
                    disabled: !selectedExport,
                },
            ]}
        />
    </div>;
};
export default React.memo(InterventionExport);